import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getSuppliers, createPurchaseOrder, getInventoryItems } from '../services/api';
import { formatCurrency } from '../utils/formatCurrency';
import './CreatePurchaseOrderPage.css';

function CreatePurchaseOrderPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    supplierId: '',
    expectedDate: '',
    notes: '',
    items: [],
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [suppliersData, inventoryData] = await Promise.all([
        getSuppliers(),
        getInventoryItems(),
      ]);
      setSuppliers(suppliersData.suppliers || []);
      setInventoryItems(inventoryData.items || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { itemId: '', name: '', quantity: 1, unit: 'un', price: 0 }],
    });
  };

  const handleRemoveItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;

    // If changing inventory item selection, autofill other fields
    if (field === 'itemId' && value) {
      const selectedItem = inventoryItems.find((i) => i.itemId === value);
      if (selectedItem) {
        newItems[index].name = selectedItem.name;
        newItems[index].unit = selectedItem.unit;
        newItems[index].price = selectedItem.cost || 0;
      }
    }

    setFormData({ ...formData, items: newItems });
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => sum + item.quantity * item.price, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createPurchaseOrder({
        ...formData,
        total: calculateTotal(),
      });
      navigate('/inventory/purchase-orders');
    } catch (error) {
      console.error('Error creating order:', error);
      alert('Error creating order');
    }
  };

  if (loading) return <div className="loading">{t('common.loading')}</div>;

  return (
    <div className="create-purchase-order-page">
      <div className="page-header mb-4">
        <h1>{t('purchaseOrders.newOrder', 'Nueva Orden de Compra')}</h1>
      </div>

      <form onSubmit={handleSubmit} className="card p-4">
        <div className="form-group mb-4">
          <label>{t('suppliers.supplier', 'Proveedor')}</label>
          <select
            value={formData.supplierId}
            onChange={(e) => setFormData({ ...formData, supplierId: e.target.value })}
            required
            className="form-control"
          >
            <option value="">{t('common.select')}</option>
            {suppliers.map((s) => (
              <option key={s.supplierId} value={s.supplierId}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-row mb-4" style={{ display: 'flex', gap: '20px' }}>
          <div className="form-group" style={{ flex: 1 }}>
            <label>{t('purchaseOrders.expectedDate', 'Fecha Esperada')}</label>
            <input
              type="date"
              value={formData.expectedDate}
              onChange={(e) => setFormData({ ...formData, expectedDate: e.target.value })}
              className="form-control"
            />
          </div>
        </div>

        <div className="items-section mb-4">
          <h2>{t('purchaseOrders.items', 'Items')}</h2>
          <div className="table-responsive">
            <table className="table w-100">
              <thead>
                <tr>
                  <th style={{ width: '25%' }}>
                    {t('common.inventoryItem', 'Item Inventario (Opcional)')}
                  </th>
                  <th>{t('common.item', 'Descripción / Nombre')}</th>
                  <th style={{ width: '100px' }}>{t('common.quantity', 'Cant.')}</th>
                  <th style={{ width: '100px' }}>{t('common.unit', 'Unidad')}</th>
                  <th style={{ width: '120px' }}>{t('common.price', 'Precio Est.')}</th>
                  <th style={{ width: '120px' }}>{t('common.subtotal', 'Subtotal')}</th>
                  <th style={{ width: '50px' }}></th>
                </tr>
              </thead>
              <tbody>
                {formData.items.map((item, index) => (
                  <tr key={index}>
                    <td data-label={t('common.inventoryItem', 'Item Inventario')}>
                      <select
                        className="form-control"
                        value={item.itemId || ''}
                        onChange={(e) => handleItemChange(index, 'itemId', e.target.value)}
                      >
                        <option value="">-- Manual --</option>
                        {inventoryItems.map((invItem) => (
                          <option key={invItem.itemId} value={invItem.itemId}>
                            {invItem.name} ({invItem.unit})
                          </option>
                        ))}
                      </select>
                    </td>
                    <td data-label={t('common.item', 'Descripción')}>
                      <input
                        type="text"
                        value={item.name}
                        onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                        required
                        className="form-control"
                      />
                    </td>
                    <td data-label={t('common.quantity', 'Cant.')}>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) =>
                          handleItemChange(index, 'quantity', parseFloat(e.target.value))
                        }
                        required
                        min="0.1"
                        step="0.1"
                        className="form-control"
                      />
                    </td>
                    <td data-label={t('common.unit', 'Unidad')}>
                      <select
                        value={item.unit}
                        onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                        className="form-control"
                      >
                        <option value="un">un</option>
                        <option value="kg">kg</option>
                        <option value="lt">lt</option>
                        <option value="pack">pack</option>
                      </select>
                    </td>
                    <td data-label={t('common.price', 'Precio')}>
                      <input
                        type="number"
                        value={item.price}
                        onChange={(e) =>
                          handleItemChange(index, 'price', parseFloat(e.target.value))
                        }
                        min="0"
                        step="0.01"
                        className="form-control"
                      />
                    </td>
                    <td data-label={t('common.subtotal', 'Subtotal')}>
                      {formatCurrency(item.quantity * item.price)}
                    </td>
                    <td className="po-item-remove">
                      <button
                        type="button"
                        className="btn-icon text-danger"
                        onClick={() => handleRemoveItem(index)}
                        aria-label={t('common.delete')}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" className="btn btn-secondary mt-2" onClick={handleAddItem}>
            + {t('purchaseOrders.addItem', 'Agregar Item')}
          </button>
        </div>

        <div className="order-summary mb-4 text-right" style={{ textAlign: 'right' }}>
          <h2>
            {t('common.total', 'Total')}: {formatCurrency(calculateTotal())}
          </h2>
        </div>

        <div className="form-group mb-4">
          <label>{t('common.notes', 'Notas')}</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            className="form-control"
            rows="3"
          />
        </div>

        <div
          className="form-actions"
          style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}
        >
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => navigate('/inventory/purchase-orders')}
          >
            {t('common.cancel')}
          </button>
          <button type="submit" className="btn btn-primary">
            {t('purchaseOrders.createOrder', 'Crear Orden')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CreatePurchaseOrderPage;
